import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useRequest } from 'ahooks';
import { message } from 'antd';
import { ApplyProduct } from 'components/Dialogue/components/QAPair/ApplyProduct';
import { useQueryQAPairProduct } from 'components/Dialogue/components/QAPair/useQAPairProduct';
import { useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import {
  getExpandSpus,
  SupplementalKnowledgeBindItem,
  SupplementKnowledgeRelatedSPU,
} from 'services/api/supplementKnowledge';
import { ExpandSkuDrawer } from '../SpuMapping/ExpandSkuDrawer';
import useModal from 'hooks/useModal';

const spu2bindItem = (item: SupplementKnowledgeRelatedSPU): SupplementalKnowledgeBindItem => {
  return {
    ...item,
    platform: item.platform as any,
    region: item.region as any,
    skus: item.skus,
  };
};

function AssisKnowledgeItem({
  knowledgeId,
  storeId,
  itemIdentifier,
  bindItem,
  onBindItemChange,
}: {
  knowledgeId: number;
  storeId: number;
  itemIdentifier: SupplementKnowledgeRelatedSPU;
  bindItem: SupplementalKnowledgeBindItem[];
  onBindItemChange: (bindItem: SupplementalKnowledgeBindItem[]) => void;
}) {
  const { t } = useTranslate();

  const { itemInternalId, spuId, platform, region, skus } = itemIdentifier;

  const { seedItem, seedSkuIds, expandItems, expandSkuCount } = useMemo(() => {
    const seedItem = bindItem.find((_) => _.itemInternalId === itemInternalId);
    const seedSkuIds = skus
      .filter((_) => seedItem?.skus.some(({ skuId }) => skuId === _.skuId))
      .map((_) => _.skuId);
    const expandItems = bindItem.filter((_) => _.itemInternalId !== itemInternalId);
    const expandSkuCount = expandItems.reduce((res, { skus }) => res + skus.length, 0);
    return { seedItem, seedSkuIds, expandItems, expandSkuCount };
  }, [bindItem, itemInternalId, skus]);

  const [selectedSeedSkuIds, setSelectedSeedSkuIds] = useState(seedSkuIds);

  const { isLoading, product, selectable, defaultApplyType } = useQueryQAPairProduct({
    enable: true,
    itemId: spuId,
    storeId: storeId.toString(),
    channel: platform as any,
    region: region as any,
    selectedSkus: seedSkuIds,
  });

  const { loading, runAsync } = useRequest(
    (skuIds: string[] = seedSkuIds) => {
      return getExpandSpus([
        {
          ...itemIdentifier,
          skus: itemIdentifier.skus.filter((_) => skuIds.includes(_.skuId)),
        },
      ]);
    },
    {
      manual: true,
      onError: (knowledgeError) => message.error(
          describeKnowledgeApiError(
            knowledgeError,
            undefined,
            t('phrase.获取数据失败，请稍后再试'),
          ),
        ),
    },
  );

  const { visible, show, hide } = useModal();

  if (!product || !defaultApplyType) {
    return isLoading ? (
      <div className="flex w-full items-center justify-end">
        <LoadingOutlined />
      </div>
    ) : (
      <></>
    );
  }

  const {
    item: { imageUrl, skuInfo },
  } = product;

  const seed = (
    <ApplyProduct
      v2
      imageUrl={imageUrl[0]}
      skuInfo={skuInfo}
      defaultApplyType={defaultApplyType}
      showImage={false}
      skuSelected={selectedSeedSkuIds}
      selectable={selectable}
      onSKUChange={setSelectedSeedSkuIds}
      size="small"
      onHide={() => {
        const nextSkuIds = selectedSeedSkuIds.length
          ? selectedSeedSkuIds
          : skuInfo.map(({ skuId }) => skuId);
        if (!isEqual(nextSkuIds, seedSkuIds)) {
          let seedItem: SupplementalKnowledgeBindItem;
          onBindItemChange(
            bindItem.map((item) => {
              if (item.itemInternalId !== itemInternalId) {
                return item;
              }
              seedItem = {
                ...item,
                skus: nextSkuIds.reduce<SupplementalKnowledgeBindItem['skus']>((res, skuId) => {
                  const sku = skuInfo.find((_) => _.skuId === skuId);
                  if (sku) {
                    res.push({
                      skuId: sku.skuId,
                      skuName: sku.skuName || '',
                      sellerSku: sku.sellerSku,
                    });
                  }
                  return res;
                }, []),
              };
              return seedItem;
            }),
          );
          runAsync(nextSkuIds).then((data) => {
            onBindItemChange([...(seedItem ? [seedItem] : []), ...data.map(spu2bindItem)]);
          });
        }
      }}
    />
  );

  const expand = loading ? (
    t('计算中')
  ) : (
    <span className="cursor-pointer" onClick={show}>
      {t('扩展同款商品xspuysku', {
        x: expandItems.length,
        y: expandSkuCount,
      })}
      <RightOutlined />
    </span>
  );

  const drawer = (
    <ExpandSkuDrawer
      key={`${visible}`}
      knowledgeId={knowledgeId}
      bindItem={expandItems}
      visible={visible}
      onClose={hide}
      onBindItemChange={(bindItem) =>
        onBindItemChange([...(seedItem ? [seedItem] : []), ...bindItem])
      }
    />
  );

  return (
    <div className="flex w-full items-center justify-end">
      <div className="text-[var(--fett-scenario-accent-text)]">{expand}</div>
      <div className="w-[1px] h-[14px] bg-[#336fff] mx-1" />
      {seed}
      {drawer}
    </div>
  );
}

export default AssisKnowledgeItem;
